import { RuleMode } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import type { TriggerUserPreferences } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/users';
import { Location, type UUID } from '@cobliteam/common-types';
import { alertHookConfig, resolveConditionId } from '@cobliteam/hooks/src/useAlertHooks/alertHookConfig';
import type { CurrentAlertHook } from '@cobliteam/hooks/src/useAlertHooks/types';
import useAlertHooksStore from '@cobliteam/hooks/src/useAlertHooks/useAlertHooksStore';
import { useCreateTrigger, useGetRules } from '@cobliteam/hooks/src/useEntity/useAlerts';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { useTranslation } from '@cobliteam/i18n';
import { Button, Dialog, Typography } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import queryString from 'query-string';
import { useRef } from 'react';
import { trackActivated, trackEditClicked } from './analytics';
import SummaryRow from './SummaryRow';

interface DialogAlertHookConfirmProps {
  current: CurrentAlertHook;
}

/**
 * Step 2 — read-only preview of the pre-configured trigger. "Ativar alerta" creates it and closes
 * (the success toast is emitted by `useCreateTrigger`'s mutation meta — the global mutation cache
 * picks it up, so this component intentionally doesn't toast); "Editar antes de ativar" opens the
 * trigger create page pre-filled via query params instead of creating anything — only variable
 * content (entity ids) travels in the URL, everything preset by the template is re-derived from
 * `alertHookConfig` on the other side.
 */
const DialogAlertHookConfirm = ({ current }: DialogAlertHookConfirmProps) => {
  const { t } = useTranslation(['alertHooks']);
  const close = useAlertHooksStore((state) => state.close);
  const { user } = useUser();
  const { data: rules } = useGetRules(RuleMode.FLEET_POLICY);
  const { mutateAsync: createTrigger, isPending: isCreating } = useCreateTrigger();

  // Synchronous re-entry guard: `isCreating` from useMutation only flips on the next render, so back-to-back
  // clicks before React commits would each fire a create request. The ref blocks that without an extra render.
  const isHandlingActivateRef = useRef(false);

  const config = alertHookConfig[current.type];
  const { copy } = config;
  const summaryRows = config.buildSummaryRows(current.params);
  const dismissKey = config.getDismissKey(current.params);

  const handleActivate = async () => {
    if (isHandlingActivateRef.current) return;

    const conditionId = resolveConditionId(rules, current.type, config.conditionCategoryDefault);
    if (!conditionId) return; // rule/condition unavailable — same gate as useTriggerAlertHook; button stays inert.

    const defaultChannels: TriggerUserPreferences[] = user
      ? [{ id: user.id, name: (user as { name?: string }).name ?? '', sendNotificationWebPainel: true, sendNotificationAppGestor: true, sendNotificationEmail: true }]
      : [];

    const payload = config.buildPayload(current.params, {
      name: t(copy.defaultNameKey),
      conditionCategory: config.conditionCategoryDefault,
      allowNotifications: true,
      notifiedUsersChannels: defaultChannels,
    });

    isHandlingActivateRef.current = true;
    try {
      await createTrigger({ ...payload, conditionId });
      trackActivated({ hookType: current.type, dismissKey });
      close();
    } catch {
      // Toast handled by the mutation's `meta.errorMessage`. Keep the modal open so the user can retry.
    } finally {
      isHandlingActivateRef.current = false;
    }
  };

  const handleEdit = () => {
    trackEditClicked({ hookType: current.type, dismissKey });
    // Pre-fill the create page through the template contract: rule/condition identify the template
    // (recurrence, name and the remaining defaults are derived there from `alertHookConfig`), and
    // only the journey's entity ids are forwarded. `geofenceId` is singular because that's the
    // param `TriggerGeofencesSelect` already prefills from (hooks always carry one geofence).
    const { vehicleIds, vehicleGroupIds, geofenceIds } = current.params as Partial<Record<'vehicleIds' | 'vehicleGroupIds' | 'geofenceIds', UUID[]>>;
    const target = `${Location.ALERTS_TRIGGERS_CREATE}?${queryString.stringify({
      templateRule: config.ruleCategory,
      templateCondition: config.conditionCategoryDefault,
      vehicleIds,
      vehicleGroupIds,
      geofenceId: geofenceIds?.[0],
    })}`;
    window.open(target, '_blank', 'noopener,noreferrer');
    close();
  };

  return (
    <Dialog.Content size='sm' className='flex flex-col gap-0 p-0'>
      <Dialog.Body className='px-6 pt-2 pb-4'>
        <div className='mb-4 flex flex-col items-center justify-center pt-8 text-center'>
          <div className={`flex h-14 w-14 items-center justify-center rounded-full ${copy.suggestion.iconBg}`}>
            <Icon name={copy.suggestion.iconName as never} size='lg' color='primary' />
          </div>
          <Dialog.Title className='mt-4 text-center'>{t(copy.confirm.titleKey)}</Dialog.Title>
          <Dialog.Description asChild>
            <Typography variant='text-sm' color='secondary' className='mt-2 px-6 text-center'>
              {t('alertHooks:common.confirmDescription')}
            </Typography>
          </Dialog.Description>
        </div>

        <div className='overflow-hidden rounded-2xl border border-gray-6'>
          {summaryRows.map((row, index) => (
            <SummaryRow key={row.labelKey} label={t(row.labelKey)} value={t(row.valueKey, row.valueParams)} withDivider={index < summaryRows.length - 1} />
          ))}
          <button
            type='button'
            onClick={handleEdit}
            className='flex w-full items-center justify-center gap-2 border-gray-6 border-t bg-white px-4 py-3 text-primary hover:cursor-pointer hover:bg-gray-2'
          >
            <Icon name='pencil' size='sm' />
            <Typography variant='text-sm-medium' color='primary'>
              {t('alertHooks:common.ctaEditBeforeActivate')}
            </Typography>
            <Icon name='external-link' size='sm' />
          </button>
        </div>
      </Dialog.Body>

      <Dialog.Footer className='flex flex-col gap-2 px-6 pt-4'>
        <Button onClick={handleActivate} disabled={isCreating} className='w-full'>
          {t('alertHooks:common.ctaActivate')}
        </Button>
        <Button onClick={close} variant='ghost' className='w-full'>
          {t('alertHooks:common.ctaClose')}
        </Button>
      </Dialog.Footer>
    </Dialog.Content>
  );
};

export default DialogAlertHookConfirm;
